/**
 * Guided tour (Move 01 follow-up) — 4-step coachmark sequence
 * pointing at the studio's core surfaces. Triggered by the boot
 * overlay's "Take the tour" choice, or re-launched from the
 * Toolbar ⋯ → "Help / tour" menu item.
 *
 * Per-step rendering:
 *   - The target element gets a phosphor outline + soft glow,
 *     drawn as a fixed-position div sized to its bounding rect.
 *     This is purely visual; the target's own DOM is untouched.
 *   - A tooltip card sits adjacent to the target (below by
 *     default, flipping to above when there isn't viewport
 *     room). Carries a step counter, title, body copy, and
 *     "Skip" / "Next" / "Finish" actions.
 *
 * Position tracking: a window-level `resize` + scroll listener
 * + a `requestAnimationFrame` driven recalculation on each
 * step change, so the coachmark stays pinned to its target as
 * the user resizes / scrolls. Targets are looked up via
 * `data-tour` attributes — components opt in by tagging the
 * element they want pointed at.
 *
 * Persistence: completion (Next on the last step) or Skip
 * sets `cas:tourSeen` in localStorage so the tour doesn't
 * auto-trigger on subsequent boot-overlay tours. The menu
 * entry deliberately clears the flag and re-launches so the
 * user can replay it on demand.
 */
import {
	useEffect,
	useLayoutEffect,
	useState,
	type CSSProperties
} from 'react';
import { createPortal } from 'react-dom';
import { PANEL, PANEL_BORDER, PHOSPHOR, AMBER, CYAN, DIM, TEXT, VOID } from '../lib/theme';

const STORAGE_KEY = 'cas:tourSeen';

/** Step definitions. Edit order or copy here in one place. */
interface TourStep {
	/**
	 * Stable id passed to `onStepActive` so the host can set up
	 * the selection / view state a step needs (e.g. the
	 * connection step opens the EdgeEditor). Distinct from
	 * `selector`, which is the DOM target to highlight.
	 */
	id: string;
	selector: string;
	/**
	 * Optional second selector whose rect is UNIONED with the
	 * primary, so the highlight + spotlight cutout span both.
	 * Used by the connection step to frame the selected wire
	 * AND its editor card together.
	 */
	unionSelector?: string;
	title: string;
	body: string;
	/** Which side of the target the tooltip prefers. Falls back if no room. */
	prefer?: 'top' | 'bottom' | 'left' | 'right';
}

const STEPS: TourStep[] = [
	{
		id: 'scene',
		selector: '[data-tour="scene-node"]',
		title: 'Scenes are nodes',
		body: 'Each card is one scene. Click it to open an inline editor right next to it — heading, narration, scene items, choices. Drag the ⠿ grip to reorder choices. Adventure-wide settings (tiers, item catalogue, share) live in the ⋯ menu.',
		prefer: 'right'
	},
	{
		id: 'items',
		selector: '[data-tour="scene-items"]',
		title: 'Scenes can hold items',
		body: 'Define items in Adventure settings, then drop them into scenes here. In a playtest the player takes them, and choices can require / consume / grant items — so a path can stay hidden until the player is carrying the right thing. The lantern in this example unlocks a darker route later.',
		prefer: 'left'
	},
	{
		id: 'connection',
		selector: '[data-tour="edge-editor"]',
		// Union the editor with the selected wire so the spotlight
		// shows BOTH — the editor now sits beside the wire rather
		// than on top of it, so you can see which connection it's
		// editing.
		unionSelector: '.react-flow__edge.selected',
		title: 'Connections carry the reaction',
		body: 'Selecting a connection (or hitting the ✎ on a choice) opens this editor beside the wire it edits. A connection IS the choice: here you set the reaction (what the player reads right after choosing), the points, the next scene, and any item gating (require / consume / grant).',
		prefer: 'bottom'
	},
	{
		id: 'view-toggle',
		selector: '[data-tour="view-toggle"]',
		title: 'Prefer writing to wiring?',
		body: 'This toggle switches the whole studio between three surfaces: graph (the node canvas you\'re in), write (the same adventure as an editable script document — scenes as prose, choices as a list), and split (both at once). Everything edits the same JSON, so flip freely.',
		prefer: 'bottom'
	},
	{
		id: 'playtest',
		selector: '[data-tour="right-panel"]',
		title: 'Playtest alongside',
		body: 'The right panel runs a live playtest. The graph mirrors the run — current scene glows cyan, visited scenes dim, the taken path goes cyan. Editing a scene never loses your place mid-run.',
		prefer: 'left'
	},
	{
		id: 'ship',
		selector: '[data-tour="ship-button"]',
		title: 'Ship the JSON',
		body: 'When you\'re happy, Ship validates the adventure and downloads the JSON, ready to drop into a console-adventure project.',
		prefer: 'bottom'
	}
];

export function hasSeenTour(): boolean {
	try {
		return localStorage.getItem(STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
}

export function markTourSeen(): void {
	try {
		localStorage.setItem(STORAGE_KEY, 'true');
	} catch {
		// Silently no-op; the tour is non-critical.
	}
}

export function clearTourSeen(): void {
	try {
		localStorage.removeItem(STORAGE_KEY);
	} catch {
		// Silently no-op.
	}
}

interface Props {
	onClose: () => void;
	/**
	 * Fired with the active step's `id` whenever the step
	 * changes (including the first step on mount). Lets the host
	 * set up the selection / view state a step needs — e.g. the
	 * connection step opens the EdgeEditor so the tour can
	 * highlight it. Optional: a host that doesn't drive state
	 * can omit it and the tour just highlights whatever's there.
	 */
	onStepActive?: (id: string) => void;
}

interface AnchorRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

const TOOLTIP_WIDTH = 320;
// Offset must clear the highlight halo (HALO = 14 inside Tour
// below) plus a few px of breathing room, otherwise the
// tooltip's edge touches the highlight outline.
const TOOLTIP_OFFSET = 22;
const VIEWPORT_MARGIN = 16;

export function Tour({ onClose, onStepActive }: Props) {
	const [stepIndex, setStepIndex] = useState(0);
	const [anchor, setAnchor] = useState<AnchorRect | null>(null);
	const step = STEPS[stepIndex];

	// Tell the host which step is active so it can set up the
	// selection / view state that step's target needs (e.g. the
	// connection step opens the EdgeEditor). Fires on every step
	// change including the first. `onStepActive` is intentionally
	// excluded from deps — a host that recreates the callback
	// each render would otherwise refire on unrelated renders.
	useEffect(() => {
		onStepActive?.(step.id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepIndex]);

	// Re-measure on step change, window resize, and scroll, with
	// a short rAF retry loop. The retry matters because some
	// targets mount ASYNC after the step's `onStepActive` sets
	// up state — the connection step opens the EdgeEditor, which
	// isn't in the DOM until React commits that selection. We
	// poll a handful of frames before falling back to the
	// centred "couldn't find it" card.
	useLayoutEffect(() => {
		let raf = 0;
		let attempts = 0;
		const MAX_ATTEMPTS = 15; // ~250ms at 60fps
		const tick = () => {
			const el = step
				? document.querySelector<Element>(step.selector)
				: null;
			if (el) {
				const a = el.getBoundingClientRect();
				let left = a.left;
				let top = a.top;
				let right = a.right;
				let bottom = a.bottom;
				// Union with the optional second target so the
				// cutout frames both (e.g. wire + its editor).
				if (step.unionSelector) {
					const el2 = document.querySelector<Element>(step.unionSelector);
					if (el2) {
						const b = el2.getBoundingClientRect();
						left = Math.min(left, b.left);
						top = Math.min(top, b.top);
						right = Math.max(right, b.right);
						bottom = Math.max(bottom, b.bottom);
					}
				}
				setAnchor({ x: left, y: top, width: right - left, height: bottom - top });
				return;
			}
			if (attempts < MAX_ATTEMPTS) {
				attempts += 1;
				raf = requestAnimationFrame(tick);
			} else {
				setAnchor(null); // give up → fallback card
			}
		};
		raf = requestAnimationFrame(tick);
		const onTick = () => {
			cancelAnimationFrame(raf);
			attempts = 0;
			raf = requestAnimationFrame(tick);
		};
		window.addEventListener('resize', onTick);
		window.addEventListener('scroll', onTick, true);
		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener('resize', onTick);
			window.removeEventListener('scroll', onTick, true);
		};
	}, [step]);

	// Esc skips the tour; ← / → walk between steps.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				handleSkip();
				return;
			}
			if (e.key === 'ArrowRight') {
				e.preventDefault();
				handleNext();
				return;
			}
			if (e.key === 'ArrowLeft' && stepIndex > 0) {
				e.preventDefault();
				setStepIndex(stepIndex - 1);
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
		// handlers close over stepIndex; the listener rebinds
		// when it changes so each step's Right-arrow advances
		// correctly.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stepIndex]);

	const handleSkip = () => {
		markTourSeen();
		onClose();
	};
	const handleNext = () => {
		if (stepIndex === STEPS.length - 1) {
			markTourSeen();
			onClose();
		} else {
			setStepIndex(stepIndex + 1);
		}
	};

	// Highlight rectangle around the target. Padded out so the
	// outline breathes around the target instead of hugging it
	// flush -- a 14px halo around a scene node reads as "look
	// here," whereas a tight outline reads as a re-selection.
	const HALO = 14;
	const highlightStyle: CSSProperties | null = anchor
		? {
				position: 'fixed',
				left: anchor.x - HALO,
				top: anchor.y - HALO,
				width: anchor.width + HALO * 2,
				height: anchor.height + HALO * 2,
				border: `2px solid ${PHOSPHOR}`,
				borderRadius: 14,
				boxShadow: `0 0 0 6px ${PHOSPHOR}22, 0 0 36px ${PHOSPHOR}66`,
				pointerEvents: 'none',
				zIndex: 200,
				transition: 'left 200ms ease, top 200ms ease, width 200ms ease, height 200ms ease'
		  }
		: null;

	// Cutout edges for the spotlight mask — matches the
	// highlight halo so the un-blurred hole aligns with the
	// glowing outline. The four dim+blur panels below frame this
	// rect, leaving it crisp while everything else recedes.
	const cut = anchor
		? {
				left: anchor.x - HALO,
				top: anchor.y - HALO,
				right: anchor.x + anchor.width + HALO,
				bottom: anchor.y + anchor.height + HALO
		  }
		: null;

	// Tooltip position. Prefer the step's `prefer` side; fall
	// back to the side with the most viewport room when the
	// preferred side overflows.
	const tooltipStyle: CSSProperties | null = (() => {
		if (!anchor) return null;
		const winW = window.innerWidth;
		const winH = window.innerHeight;
		const prefer = step.prefer ?? 'bottom';

		const spaceTop = anchor.y;
		const spaceBottom = winH - (anchor.y + anchor.height);
		const spaceLeft = anchor.x;
		const spaceRight = winW - (anchor.x + anchor.width);

		// Estimate tooltip height; the card scales with body
		// length but ~150px is a safe upper bound for our copy.
		const tooltipHeight = 170;
		const place = (side: 'top' | 'bottom' | 'left' | 'right'): CSSProperties => {
			switch (side) {
				case 'top':
					return {
						position: 'fixed',
						left: clamp(
							anchor.x + anchor.width / 2 - TOOLTIP_WIDTH / 2,
							VIEWPORT_MARGIN,
							winW - TOOLTIP_WIDTH - VIEWPORT_MARGIN
						),
						top: clamp(anchor.y - tooltipHeight - TOOLTIP_OFFSET, VIEWPORT_MARGIN, winH),
						width: TOOLTIP_WIDTH
					};
				case 'bottom':
					return {
						position: 'fixed',
						left: clamp(
							anchor.x + anchor.width / 2 - TOOLTIP_WIDTH / 2,
							VIEWPORT_MARGIN,
							winW - TOOLTIP_WIDTH - VIEWPORT_MARGIN
						),
						top: anchor.y + anchor.height + TOOLTIP_OFFSET,
						width: TOOLTIP_WIDTH
					};
				case 'left':
					return {
						position: 'fixed',
						left: clamp(
							anchor.x - TOOLTIP_WIDTH - TOOLTIP_OFFSET,
							VIEWPORT_MARGIN,
							winW - TOOLTIP_WIDTH - VIEWPORT_MARGIN
						),
						top: clamp(
							anchor.y + anchor.height / 2 - tooltipHeight / 2,
							VIEWPORT_MARGIN,
							winH - tooltipHeight - VIEWPORT_MARGIN
						),
						width: TOOLTIP_WIDTH
					};
				case 'right':
					return {
						position: 'fixed',
						left: anchor.x + anchor.width + TOOLTIP_OFFSET,
						top: clamp(
							anchor.y + anchor.height / 2 - tooltipHeight / 2,
							VIEWPORT_MARGIN,
							winH - tooltipHeight - VIEWPORT_MARGIN
						),
						width: TOOLTIP_WIDTH
					};
			}
		};

		const fits = {
			top: spaceTop > tooltipHeight + TOOLTIP_OFFSET + VIEWPORT_MARGIN,
			bottom: spaceBottom > tooltipHeight + TOOLTIP_OFFSET + VIEWPORT_MARGIN,
			left: spaceLeft > TOOLTIP_WIDTH + TOOLTIP_OFFSET + VIEWPORT_MARGIN,
			right: spaceRight > TOOLTIP_WIDTH + TOOLTIP_OFFSET + VIEWPORT_MARGIN
		};

		if (fits[prefer]) return place(prefer);
		// Fallback: pick the side with the most absolute room.
		const candidates: Array<['top' | 'bottom' | 'left' | 'right', number]> = [
			['bottom', spaceBottom],
			['top', spaceTop],
			['right', spaceRight],
			['left', spaceLeft]
		];
		candidates.sort((a, b) => b[1] - a[1]);
		return place(candidates[0][0]);
	})();

	return createPortal(
		<>
			{/* Spotlight mask. When a target is anchored, four
			    dim + blurred panels frame the cutout rect so
			    everything OUTSIDE the highlight recedes — this is
			    what stops overlapping editor cards (inline scene
			    card, connection editor) from competing with the
			    thing the step is pointing at. With no anchor
			    (fallback card) we dim+blur the whole viewport.
			    All panels are pointer-events:none so the user can
			    still pan / interact through them. */}
			{cut ? (
				<Spotlight cut={cut} />
			) : (
				<div
					style={{
						position: 'fixed',
						inset: 0,
						background: `${VOID}aa`,
						backdropFilter: 'blur(2.5px)',
						WebkitBackdropFilter: 'blur(2.5px)',
						zIndex: 150,
						pointerEvents: 'none'
					}}
				/>
			)}
			{highlightStyle && <div style={highlightStyle} />}
			{tooltipStyle && (
				<TooltipCard
					style={tooltipStyle}
					step={step}
					stepIndex={stepIndex}
					total={STEPS.length}
					onPrev={stepIndex > 0 ? () => setStepIndex(stepIndex - 1) : undefined}
					onSkip={handleSkip}
					onNext={handleNext}
				/>
			)}
			{/* Missing-target fallback — if the selector returns
			    nothing (e.g. the user is in write mode and the
			    inspect panel isn't rendered), show a centred
			    notice + advance. Stops the tour from silently
			    appearing to hang. */}
			{!anchor && (
				<TooltipCard
					style={{
						position: 'fixed',
						left: '50%',
						top: '50%',
						transform: 'translate(-50%, -50%)',
						width: TOOLTIP_WIDTH
					}}
					step={{
						...step,
						body: `${step.body}\n\n(Couldn't find the target on this view — switch back to the graph view to see it highlighted.)`
					}}
					stepIndex={stepIndex}
					total={STEPS.length}
					onPrev={stepIndex > 0 ? () => setStepIndex(stepIndex - 1) : undefined}
					onSkip={handleSkip}
					onNext={handleNext}
				/>
			)}
		</>,
		document.body
	);
}

/* ─── tooltip ───────────────────────────────────────────── */

function TooltipCard({
	style,
	step,
	stepIndex,
	total,
	onPrev,
	onSkip,
	onNext
}: {
	style: CSSProperties;
	step: TourStep;
	stepIndex: number;
	total: number;
	onPrev?: () => void;
	onSkip: () => void;
	onNext: () => void;
}) {
	const isLast = stepIndex === total - 1;
	return (
		<div
			role="dialog"
			aria-label={`Tour step ${stepIndex + 1} of ${total}`}
			style={{
				...style,
				background: PANEL,
				border: `1px solid ${PHOSPHOR}`,
				borderRadius: 10,
				boxShadow: `0 0 0 1px ${PHOSPHOR}22, 0 22px 60px #000000cc`,
				color: TEXT,
				fontFamily: 'ui-monospace, "JetBrains Mono", monospace',
				padding: '14px 16px 12px',
				zIndex: 201
			}}
		>
			<div
				style={{
					display: 'flex',
					alignItems: 'baseline',
					justifyContent: 'space-between',
					marginBottom: 6
				}}
			>
				<span
					style={{
						color: CYAN,
						fontSize: 9,
						fontWeight: 700,
						letterSpacing: '0.12em'
					}}
				>
					STEP {stepIndex + 1} / {total}
				</span>
				<button
					onClick={onSkip}
					style={{
						background: 'transparent',
						border: 'none',
						color: DIM,
						fontFamily: 'inherit',
						fontSize: 10,
						cursor: 'pointer',
						padding: 0
					}}
					title="Skip the tour"
				>
					Skip
				</button>
			</div>
			<div
				style={{
					color: PHOSPHOR,
					fontSize: 13,
					fontWeight: 700,
					letterSpacing: '0.02em',
					marginBottom: 6
				}}
			>
				{step.title}
			</div>
			<div
				style={{
					color: TEXT,
					fontSize: 11.5,
					lineHeight: 1.55,
					marginBottom: 12,
					whiteSpace: 'pre-line'
				}}
			>
				{step.body}
			</div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 8
				}}
			>
				<div style={{ display: 'flex', gap: 4 }}>
					{Array.from({ length: total }).map((_, i) => (
						<span
							key={i}
							style={{
								width: 5,
								height: 5,
								borderRadius: 3,
								background: i === stepIndex ? PHOSPHOR : PANEL_BORDER
							}}
						/>
					))}
				</div>
				<div style={{ display: 'flex', gap: 6 }}>
					{onPrev && (
						<TourButton onClick={onPrev} tone="ghost">
							‹ Back
						</TourButton>
					)}
					<TourButton onClick={onNext} tone="primary">
						{isLast ? 'Finish' : 'Next ›'}
					</TourButton>
				</div>
			</div>
		</div>
	);
}

function TourButton({
	tone,
	onClick,
	children
}: {
	tone: 'primary' | 'ghost';
	onClick: () => void;
	children: React.ReactNode;
}) {
	const color = tone === 'primary' ? VOID : DIM;
	const background = tone === 'primary' ? PHOSPHOR : 'transparent';
	const borderColor = tone === 'primary' ? PHOSPHOR : PANEL_BORDER;
	return (
		<button
			onClick={onClick}
			style={{
				background,
				color,
				border: `1px solid ${borderColor}`,
				borderRadius: 5,
				padding: '5px 11px',
				fontFamily: 'inherit',
				fontSize: 11,
				fontWeight: tone === 'primary' ? 600 : 400,
				cursor: 'pointer',
				transition: 'background 120ms, color 120ms'
			}}
			onMouseEnter={(e) => {
				if (tone === 'ghost') {
					e.currentTarget.style.color = AMBER;
				}
			}}
			onMouseLeave={(e) => {
				if (tone === 'ghost') {
					e.currentTarget.style.color = DIM;
				}
			}}
		>
			{children}
		</button>
	);
}

function clamp(v: number, lo: number, hi: number): number {
	return Math.max(lo, Math.min(v, hi));
}

/* ─── spotlight mask ───────────────────────────────────── */

/**
 * Four fixed panels (top / bottom / left / right) that frame a
 * transparent cutout rect, each dim + blurred. Leaves the
 * cutout crisp so the highlighted element stays sharp while
 * everything around it recedes. Panels carry the same easing
 * as the highlight so the spotlight glides between steps.
 *
 * Why four panels rather than one overlay with a CSS hole:
 * backdrop-filter applies to an element's own box, so a hole
 * punched via mask/clip-path wouldn't reliably skip the blur in
 * the hole across browsers. Framing the cutout with solid
 * panels sidesteps that — the cutout is simply uncovered.
 */
function Spotlight({
	cut
}: {
	cut: { left: number; top: number; right: number; bottom: number };
}) {
	const DIM = `${VOID}aa`;
	const BLUR = 'blur(3px)';
	const panel: CSSProperties = {
		position: 'fixed',
		background: DIM,
		backdropFilter: BLUR,
		WebkitBackdropFilter: BLUR,
		zIndex: 150,
		pointerEvents: 'none',
		transition: 'left 200ms ease, top 200ms ease, width 200ms ease, height 200ms ease'
	};
	const bandHeight = Math.max(0, cut.bottom - cut.top);
	return (
		<>
			{/* top */}
			<div style={{ ...panel, left: 0, top: 0, right: 0, height: Math.max(0, cut.top) }} />
			{/* bottom */}
			<div style={{ ...panel, left: 0, top: cut.bottom, right: 0, bottom: 0 }} />
			{/* left */}
			<div
				style={{ ...panel, left: 0, top: cut.top, width: Math.max(0, cut.left), height: bandHeight }}
			/>
			{/* right */}
			<div
				style={{ ...panel, left: cut.right, top: cut.top, right: 0, height: bandHeight }}
			/>
		</>
	);
}
